import { 
  collection, 
  doc, 
  getDoc,
  getDocs,
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

const CATEGORIES_COLLECTION = 'expenseCategories';
const EXPENSES_COLLECTION = 'expenses';

// ============================================
// EXPENSE CATEGORIES
// ============================================

/**
 * Create default system categories
 */
export const createDefaultCategories = async () => {
  const defaultCategories = [
    { name: 'Stock Damage', type: 'loss', affectsInventory: true, isSystem: true, color: '#f44336', icon: 'BrokenImage' },
    { name: 'Stock Theft', type: 'loss', affectsInventory: true, isSystem: true, color: '#e91e63', icon: 'Security' },
    { name: 'Stock Expiry', type: 'loss', affectsInventory: true, isSystem: true, color: '#9c27b0', icon: 'EventBusy' },
    { name: 'Supplier Return', type: 'loss', affectsInventory: true, isSystem: true, color: '#673ab7', icon: 'Undo' },
    { name: 'Sample/Giveaway', type: 'loss', affectsInventory: true, isSystem: true, color: '#3f51b5', icon: 'CardGiftcard' },
    { name: 'Customer Refund', type: 'expense', affectsInventory: false, isSystem: true, color: '#2196f3', icon: 'MoneyOff' },
    { name: 'Asset Write-Off', type: 'loss', affectsInventory: false, isSystem: true, color: '#00bcd4', icon: 'Delete' },
    { name: 'Operational', type: 'expense', affectsInventory: false, isSystem: true, color: '#009688', icon: 'Business' },
    { name: 'Petty Cash', type: 'expense', affectsInventory: false, isSystem: true, color: '#4caf50', icon: 'LocalAtm' },
    { name: 'Marketing', type: 'expense', affectsInventory: false, isSystem: true, color: '#8bc34a', icon: 'Campaign' },
    { name: 'Utilities', type: 'expense', affectsInventory: false, isSystem: true, color: '#cddc39', icon: 'Power' },
    { name: 'Salary', type: 'expense', affectsInventory: false, isSystem: true, color: '#ff9800', icon: 'Payments' },
  ];

  try {
    const promises = defaultCategories.map(category => 
      addDoc(collection(db, CATEGORIES_COLLECTION), {
        ...category,
        isArchived: false,
        createdAt: serverTimestamp()
      })
    );
    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    console.error('Error creating default categories:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create expense category
 */
export const createCategory = async (categoryData) => {
  try {
    const category = {
      name: categoryData.name,
      type: categoryData.type || 'expense',
      affectsInventory: categoryData.affectsInventory || false,
      isSystem: false,
      color: categoryData.color || '#757575',
      icon: categoryData.icon || 'Category',
      isArchived: false,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, CATEGORIES_COLLECTION), category);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe to categories
 */
export const subscribeToCategories = (callback) => {
  try {
    const q = query(
      collection(db, CATEGORIES_COLLECTION),
      orderBy('createdAt', 'asc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const categories = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      callback(categories);
    }, (error) => {
      console.error('Error subscribing to categories:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up categories subscription:', error);
    return () => {};
  }
};

/**
 * Update category
 */
export const updateCategory = async (categoryId, data) => {
  try {
    const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
    await updateDoc(categoryRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Archive category
 */
export const archiveCategory = async (categoryId) => {
  try {
    const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
    await updateDoc(categoryRef, {
      isArchived: true,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error archiving category:', error);
    return { success: false, error: error.message };
  }
};


// ============================================
// EXPENSES
// ============================================

/**
 * Create expense with stock deduction
 */
export const createExpense = async (expenseData, stockItems = []) => {
  try {
    // Calculate totals
    const totalStockLossValue = stockItems.reduce((sum, item) => sum + item.totalLoss, 0);
    const grandTotal = (parseFloat(expenseData.amount) || 0) + totalStockLossValue;

    const expense = {
      title: expenseData.title,
      categoryId: expenseData.categoryId,
      categoryName: expenseData.categoryName,
      categoryType: expenseData.categoryType,
      date: expenseData.date || new Date().toISOString().split('T')[0],
      amount: parseFloat(expenseData.amount) || 0,
      paymentMethod: expenseData.paymentMethod || 'Cash',
      referenceNo: expenseData.referenceNo || '',
      notes: expenseData.notes || '',
      attachmentUrl: expenseData.attachmentUrl || '',
      recordedBy: expenseData.recordedBy || '',
      recordedByName: expenseData.recordedByName || '',
      affectsInventory: expenseData.affectsInventory || false,
      linkedBillId: expenseData.linkedBillId || '',
      stockReturnedOnRefund: expenseData.stockReturnedOnRefund || false,
      ...(expenseData.batchDiscountData && { batchDiscountData: expenseData.batchDiscountData }),
      totalStockLossValue,
      grandTotal,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, EXPENSES_COLLECTION), expense);

    // Add stock items to subcollection and deduct from inventory
    if (stockItems.length > 0) {
      const stockPromises = stockItems.map(async (item) => {
        // Add to subcollection
        await addDoc(
          collection(db, EXPENSES_COLLECTION, docRef.id, 'stockItems'),
          {
            inventoryId: item.inventoryId,
            inventoryName: item.inventoryName,
            qtyLost: item.qtyLost,
            costPerUnit: item.costPerUnit,
            totalLoss: item.totalLoss,
            createdAt: serverTimestamp()
          }
        );

        // Deduct from inventory
        const inventoryRef = doc(db, 'inventory', item.inventoryId);
        const inventorySnap = await getDoc(inventoryRef);
        
        if (inventorySnap.exists()) {
          const currentStock = inventorySnap.data().stock || 0;
          const newStock = currentStock - item.qtyLost;
          
          await updateDoc(inventoryRef, {
            stock: newStock,
            updatedAt: serverTimestamp()
          });

          // Log stock movement
          const movementType = expenseData.categoryName.toLowerCase().includes('damage') ? 'damage' :
                              expenseData.categoryName.toLowerCase().includes('theft') ? 'theft' :
                              expenseData.categoryName.toLowerCase().includes('expiry') ? 'expiry' :
                              expenseData.categoryName.toLowerCase().includes('sample') ? 'sample' :
                              expenseData.categoryName.toLowerCase().includes('return') ? 'supplier_return' : 'loss';

          await addDoc(
            collection(db, 'inventory', item.inventoryId, 'stockMovements'),
            {
              type: movementType,
              qty: -item.qtyLost,
              referenceId: docRef.id,
              costAtTime: item.costPerUnit,
              notes: `${expenseData.categoryName}: ${expenseData.title}`,
              createdAt: serverTimestamp()
            }
          );
        }
      });

      await Promise.all(stockPromises);
    }

    return {
      success: true,
      id: docRef.id,
      message: 'Expense recorded successfully'
    };
  } catch (error) {
    console.error('Error creating expense:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Subscribe to expenses
 */
export const subscribeToExpenses = (callback) => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      orderBy('date', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const expenses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
      }));
      callback(expenses);
    }, (error) => {
      console.error('Error subscribing to expenses:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up expenses subscription:', error);
    return () => {};
  }
};

/**
 * Get expense by ID with stock items
 */
export const getExpenseById = async (expenseId) => {
  try {
    const docRef = doc(db, EXPENSES_COLLECTION, expenseId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { success: false, error: 'Expense not found' };
    }

    // Get stock items
    const stockItemsSnapshot = await getDocs(
      collection(db, EXPENSES_COLLECTION, expenseId, 'stockItems')
    );
    
    const stockItems = stockItemsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      data: {
        id: docSnap.id,
        ...docSnap.data(),
        stockItems,
        createdAt: docSnap.data().createdAt?.toDate?.() || new Date(),
        updatedAt: docSnap.data().updatedAt?.toDate?.() || new Date()
      }
    };
  } catch (error) {
    console.error('Error getting expense:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update expense
 */
export const updateExpense = async (expenseId, data) => {
  try {
    const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
    await updateDoc(expenseRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating expense:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete expense
 */
export const deleteExpense = async (expenseId) => {
  try {
    await deleteDoc(doc(db, EXPENSES_COLLECTION, expenseId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting expense:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get expenses by date range
 */
export const getExpensesByDateRange = async (startDate, endDate) => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const expenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: expenses };
  } catch (error) {
    console.error('Error getting expenses by date range:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get expenses by category
 */
export const getExpensesByCategory = async (categoryId) => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('categoryId', '==', categoryId),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const expenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: expenses };
  } catch (error) {
    console.error('Error getting expenses by category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get total loss by type
 */
export const getTotalLossByType = async (type) => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('categoryType', '==', type)
    );
    
    const snapshot = await getDocs(q);
    const total = snapshot.docs.reduce((sum, doc) => sum + (doc.data().grandTotal || 0), 0);

    return { success: true, total };
  } catch (error) {
    console.error('Error getting total loss by type:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get stock loss report
 */
export const getStockLossReport = async () => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('affectsInventory', '==', true),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const expenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const totalStockLoss = expenses.reduce((sum, exp) => sum + (exp.totalStockLossValue || 0), 0);

    return { 
      success: true, 
      data: expenses,
      totalStockLoss
    };
  } catch (error) {
    console.error('Error getting stock loss report:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all expenses
 */
export const getAllExpenses = async () => {
  try {
    const q = query(collection(db, EXPENSES_COLLECTION), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    
    const expenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
    }));

    return { success: true, data: expenses };
  } catch (error) {
    console.error('Error getting expenses:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get batch discount expenses linked to a specific invoice
 * Uses in-memory filter as fallback since nested field queries may need a Firestore index
 */
export const getDiscountExpensesByInvoice = async (invoiceId) => {
  try {
    // Try nested field query first
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('batchDiscountData.invoiceId', '==', invoiceId)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return {
        success: true,
        data: snapshot.docs.map(d => ({ id: d.id, ...d.data() })),
      };
    }
    // Fallback: scan all expenses and filter in-memory
    // (handles case where Firestore index doesn't exist for nested field)
    const allQ = query(
      collection(db, EXPENSES_COLLECTION),
      where('categoryName', '==', 'Discount Allowed')
    );
    const allSnapshot = await getDocs(allQ);
    const filtered = allSnapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(e => e.batchDiscountData?.invoiceId === invoiceId);
    return { success: true, data: filtered };
  } catch (error) {
    console.error('Error getting discount expenses by invoice:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Log a discount as an expense
 * Prevents duplicate logging by checking if discount is already logged
 */
export const logDiscountAsExpense = async (discountData) => {
  try {
    const {
      invoiceId,
      billId,
      discountAmount,
      discountType, // 'bill', 'loyalty', or 'batch'
      date,
      notes = '',
      recordedBy = '',
      recordedByName = ''
    } = discountData;

    // Check if this discount is already logged
    const existingQ = query(
      collection(db, EXPENSES_COLLECTION),
      where('linkedBillId', '==', billId || invoiceId),
      where('batchDiscountData.discountType', '==', discountType)
    );
    
    const existingSnapshot = await getDocs(existingQ);
    if (!existingSnapshot.empty) {
      // Discount already logged
      return {
        success: false,
        alreadyLogged: true,
        message: `Discount already logged for ${discountType} discount on this bill`
      };
    }

    // Get or create "Discount Allowed" category
    let discountCategoryId = null;
    const categoryQ = query(
      collection(db, CATEGORIES_COLLECTION),
      where('name', '==', 'Discount Allowed')
    );
    const categorySnapshot = await getDocs(categoryQ);
    
    if (!categorySnapshot.empty) {
      discountCategoryId = categorySnapshot.docs[0].id;
    } else {
      // Create the category if it doesn't exist
      const newCategoryRef = await addDoc(collection(db, CATEGORIES_COLLECTION), {
        name: 'Discount Allowed',
        type: 'expense',
        affectsInventory: false,
        isSystem: true,
        color: '#FFC107',
        icon: 'LocalOffer',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      discountCategoryId = newCategoryRef.id;
    }

    // Create expense entry for the discount
    const expenseData = {
      title: `Discount Allowed - ${discountType.charAt(0).toUpperCase() + discountType.slice(1)}`,
      categoryId: discountCategoryId,
      categoryName: 'Discount Allowed',
      categoryType: 'expense',
      date: date || new Date().toISOString().split('T')[0],
      amount: discountAmount,
      paymentMethod: 'N/A',
      referenceNo: billId || invoiceId || '',
      notes: notes || `${discountType} discount logged automatically`,
      attachmentUrl: '',
      recordedBy: recordedBy || '',
      recordedByName: recordedByName || '',
      affectsInventory: false,
      linkedBillId: billId || invoiceId || '',
      stockReturnedOnRefund: false,
      batchDiscountData: {
        invoiceId: invoiceId || billId,
        billId: billId || invoiceId,
        discountType: discountType,
        amount: discountAmount,
        loggedAt: serverTimestamp()
      },
      totalStockLossValue: 0,
      grandTotal: discountAmount,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, EXPENSES_COLLECTION), expenseData);

    return {
      success: true,
      id: docRef.id,
      message: `Discount expense logged successfully`
    };
  } catch (error) {
    console.error('Error logging discount as expense:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Batch log all discounts from bills and batch discounts
 * Automatically creates expense entries for all discounts that haven't been logged yet
 */
export const logAllDiscountsAsExpenses = async (bills = [], expenses = []) => {
  try {
    const results = {
      billDiscountsLogged: 0,
      batchDiscountsLogged: 0,
      alreadyLogged: 0,
      errors: 0,
      totalAmount: 0
    };

    // Log bill discounts
    for (const bill of bills) {
      // Bill discount
      if (bill.discount?.amount > 0) {
        const result = await logDiscountAsExpense({
          invoiceId: bill.invoiceNumber || bill.id,
          billId: bill.id,
          discountAmount: bill.discount.amount,
          discountType: 'bill',
          date: bill.date || bill.createdAt?.split('T')[0],
          notes: `Bill discount from invoice ${bill.invoiceNumber || bill.id}`,
          recordedBy: bill.recordedBy || '',
          recordedByName: bill.recordedByName || ''
        });

        if (result.success) {
          results.billDiscountsLogged++;
          results.totalAmount += bill.discount.amount;
        } else if (result.alreadyLogged) {
          results.alreadyLogged++;
        } else {
          results.errors++;
        }
      }

      // Loyalty discount
      if (bill.loyaltyDiscount?.amount > 0) {
        const result = await logDiscountAsExpense({
          invoiceId: bill.invoiceNumber || bill.id,
          billId: bill.id,
          discountAmount: bill.loyaltyDiscount.amount,
          discountType: 'loyalty',
          date: bill.date || bill.createdAt?.split('T')[0],
          notes: `Loyalty discount from invoice ${bill.invoiceNumber || bill.id}`,
          recordedBy: bill.recordedBy || '',
          recordedByName: bill.recordedByName || ''
        });

        if (result.success) {
          results.billDiscountsLogged++;
          results.totalAmount += bill.loyaltyDiscount.amount;
        } else if (result.alreadyLogged) {
          results.alreadyLogged++;
        } else {
          results.errors++;
        }
      }
    }

    // Log batch discounts (from expenses with batchDiscountData)
    for (const expense of expenses) {
      if (expense.batchDiscountData && !expense.isDiscountExpense) {
        // Check if this batch discount is already logged as an expense
        const existingQ = query(
          collection(db, EXPENSES_COLLECTION),
          where('linkedBillId', '==', expense.batchDiscountData.invoiceId || expense.linkedBillId),
          where('batchDiscountData.discountType', '==', 'batch')
        );
        
        const existingSnapshot = await getDocs(existingQ);
        if (existingSnapshot.empty) {
          const result = await logDiscountAsExpense({
            invoiceId: expense.batchDiscountData.invoiceId || expense.linkedBillId,
            billId: expense.linkedBillId,
            discountAmount: expense.amount,
            discountType: 'batch',
            date: expense.date,
            notes: `Batch discount: ${expense.title}`,
            recordedBy: expense.recordedBy || '',
            recordedByName: expense.recordedByName || ''
          });

          if (result.success) {
            results.batchDiscountsLogged++;
            results.totalAmount += expense.amount;
          } else if (result.alreadyLogged) {
            results.alreadyLogged++;
          } else {
            results.errors++;
          }
        } else {
          results.alreadyLogged++;
        }
      }
    }

    return {
      success: true,
      results,
      message: `Logged ${results.billDiscountsLogged + results.batchDiscountsLogged} discount expenses (${results.alreadyLogged} already logged, ${results.errors} errors)`
    };
  } catch (error) {
    console.error('Error batch logging discounts:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
